import { useEffect, useState } from "react";

/**
 * A still frame for a video thumbnail, captured once per URL.
 *
 * Rendering thumbnails as live `<video preload="metadata">` elements makes them
 * flicker: several elements share a URL with the player that is also loading it,
 * and each time that load churns the thumbnails drop and re-fetch their poster
 * frame. A captured bitmap has no such coupling — and N decoded frames cost far
 * less than N media elements.
 *
 * Cached at module scope and keyed by URL, so switching assets never recaptures.
 * Downscaled and bounded — see MAX_POSTER_PX / MAX_POSTERS below.
 */
const posters = new Map<string, string>();
const inFlight = new Map<string, Promise<string | null>>();

/** Far enough in to skip a black or faded first frame, clamped for short clips. */
const CAPTURE_TIME_SECONDS = 0.2;

/**
 * Posters are drawn at twice the filmstrip thumbnail so they stay crisp on a
 * retina display and no larger. Captured at source resolution a single 1080p
 * frame is ~150 KB of base64 to render at 56px.
 */
const MAX_POSTER_PX = 112;

/** Captures are held for the session, so the cache is bounded like any other. */
const MAX_POSTERS = 32;

/**
 * A capture that neither draws nor errors would hold its `<video>` and its
 * buffered data for the life of the page, and `inFlight` would hand every
 * later caller the same promise that never settles. A source can do that by
 * stalling, or by decoding metadata for a codec it cannot then decode frames
 * from, so the wait is bounded rather than trusted.
 */
const CAPTURE_TIMEOUT_MS = 10_000;

function capture(url: string): Promise<string | null> {
  const existing = inFlight.get(url);
  if (existing) return existing;

  const task = new Promise<string | null>((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.crossOrigin = "anonymous";
    let settled = false;

    const done = (value: string | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      video.removeAttribute("src");
      video.load();
      resolve(value);
    };

    const timeout = setTimeout(() => done(null), CAPTURE_TIMEOUT_MS);

    video.onloadeddata = () => {
      // Seeking past 0 avoids the leading frame, which is often black.
      const target = Number.isFinite(video.duration) ? Math.min(CAPTURE_TIME_SECONDS, video.duration / 2) : 0;
      if (video.currentTime < target) {
        video.currentTime = target;
        return;
      }
      draw();
    };
    video.onseeked = draw;
    video.onerror = () => done(null);

    function draw() {
      try {
        const { videoWidth, videoHeight } = video;
        if (!videoWidth || !videoHeight) return done(null);
        const scale = Math.min(1, MAX_POSTER_PX / Math.max(videoWidth, videoHeight));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(videoWidth * scale));
        canvas.height = Math.max(1, Math.round(videoHeight * scale));
        const context = canvas.getContext("2d");
        if (!context) return done(null);
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        done(canvas.toDataURL("image/jpeg", 0.7));
      } catch {
        // A cross-origin frame taints the canvas; fall back to no poster.
        done(null);
      }
    }

    video.src = url;
  }).then((poster) => {
    inFlight.delete(url);
    if (poster) {
      posters.set(url, poster);
      // Insertion-ordered, so the first key is the oldest.
      while (posters.size > MAX_POSTERS) {
        const oldest = posters.keys().next();
        if (oldest.done) break;
        posters.delete(oldest.value);
      }
    }
    return poster;
  });

  inFlight.set(url, task);
  return task;
}

/** The poster for `url`, or null until one is captured (or if capture failed). */
export function useVideoPoster(url: string | undefined): string | null {
  const [poster, setPoster] = useState<string | null>(() => (url ? (posters.get(url) ?? null) : null));

  useEffect(() => {
    if (!url) return;
    const cached = posters.get(url);
    if (cached) {
      setPoster(cached);
      return;
    }
    let active = true;
    void capture(url).then((value) => {
      if (active) setPoster(value);
    });
    return () => {
      active = false;
    };
  }, [url]);

  return poster;
}
